import { cva } from 'class-variance-authority';
import { useContext } from 'react';

import { mergeClasses } from '@/utils';

import { Badge, SuspenseImage, Tooltip, TooltipContent, TooltipTrigger } from '@/components/common';

import { TableCellProps } from './interfaces';
import TableContext from './TableContext';

const TABLE_CELL_ICON_VARIANTS = cva('', {
  variants: {
    size: {
      default: '[&_*]:size-base-plus',
      sm: '[&_*]:size-base',
      md: '[&_*]:size-m',
      lg: '[&_*]:size-m',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

const TableCell = (props: TableCellProps) => {
  const { className, children, tooltip, ...rest } = props;
  const { size } = useContext(TableContext);
  const cellType = 'cellType' in props ? props.cellType : 'text';

  const baseClasses = mergeClasses(
    'align-middle whitespace-nowrap border-b border-b-neutral-4 px-6 py-3 text-body-sm font-medium color-primary-darkest',
    className,
  );

  const renderContent = () => {
    switch (cellType) {
      case 'text-avatar': {
        const { avatarSrc, avatarAlt, avatarFallback } = props as Extract<
          TableCellProps,
          { cellType: 'text-avatar' }
        >;
        return (
          <div className="flex items-center gap-1">
            <div className="size-8 shrink-0 overflow-hidden rounded-full bg-neutral-3">
              {avatarSrc ? (
                <SuspenseImage
                  src={avatarSrc}
                  alt={avatarAlt || ''}
                  className="size-l object-cover"
                />
              ) : (
                <span className="flex size-l items-center justify-center text-body-xs font-medium text-neutral-7">
                  {avatarFallback || '?'}
                </span>
              )}
            </div>
            <span>{children}</span>
          </div>
        );
      }

      case 'text-icon': {
        const { icon, iconPosition = 'left' } = props as Extract<
          TableCellProps,
          { cellType: 'text-icon' }
        >;
        return (
          <div className="inline-flex items-center gap-1 align-middle">
            {iconPosition === 'left' && (
              <span
                className={mergeClasses(
                  TABLE_CELL_ICON_VARIANTS({ size }),
                  'shrink-0 text-primary-darkest',
                )}
              >
                {icon}
              </span>
            )}
            <span>{children}</span>
            {iconPosition === 'right' && (
              <span
                className={mergeClasses(
                  TABLE_CELL_ICON_VARIANTS({ size }),
                  'shrink-0 text-primary-darkest',
                )}
              >
                {icon}
              </span>
            )}
          </div>
        );
      }

      case 'tag': {
        const { tagVariant = 'neutral-3' } = props as Extract<TableCellProps, { cellType: 'tag' }>;
        return (
          <Badge className="items-center border-none !px-4" variant={tagVariant}>
            {children}
          </Badge>
        );
      }

      case 'icon': {
        const { icon } = props as Extract<TableCellProps, { cellType: 'icon' }>;
        return (
          <span
            className={mergeClasses(
              TABLE_CELL_ICON_VARIANTS({ size }),
              'inline-flex text-primary-darkest',
            )}
          >
            {icon}
          </span>
        );
      }

      case 'status': {
        const { statusVariant = 'primary', statusIcon } = props as Extract<
          TableCellProps,
          { cellType: 'status' }
        >;
        return (
          <Badge
            className="items-center gap-1"
            mode="light"
            size="sm"
            border
            variant={statusVariant}
          >
            {statusIcon && <span>{statusIcon}</span>}
            {children}
          </Badge>
        );
      }

      case 'text-dot': {
        const { dotColors, dotPosition = 'left' } = props as Extract<
          TableCellProps,
          { cellType: 'text-dot' }
        >;
        return (
          <div className="inline-flex items-center gap-1">
            {dotPosition === 'left' && (
              <span
                className={mergeClasses('size-2 shrink-0 rounded-full bg-primary-base', dotColors)}
              />
            )}
            <span>{children}</span>
            {dotPosition === 'right' && (
              <span
                className={mergeClasses('size-2 shrink-0 rounded-full bg-primary-base', dotColors)}
              />
            )}
          </div>
        );
      }

      case 'skeleton': {
        const { skeletonWidth = '100%' } = props as Extract<
          TableCellProps,
          { cellType: 'skeleton' }
        >;
        return (
          <div
            className="h-base-plus animate-pulse rounded bg-neutral-3"
            style={{ width: skeletonWidth }}
          />
        );
      }

      case 'custom':
      case 'text':
      default:
        return children;
    }
  };

  return (
    <td data-slot="table-cell" className={baseClasses} {...rest}>
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger className="cursor-default align-middle">{renderContent()}</TooltipTrigger>
          <TooltipContent className="text-body-sm font-medium rounded-xs" size="lg" side="bottom">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      ) : (
        renderContent()
      )}
    </td>
  );
};

export default TableCell;
